// Fila de vendas no aparelho (IndexedDB). Toda venda passa por aqui antes de ir ao servidor,
// então nada se perde se a internet cair, a aba fechar ou o celular reiniciar.
import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import type { FormaPagamento, VendaRegistrarEntrada } from "@caixaflow/shared";

export type ErroDaVenda = { codigo: string; mensagem: string; detalhes?: unknown };

export type VendaNaFila = {
  id: string;
  usuarioId: string;
  caixaId: string;
  eventoId: string;
  payload: VendaRegistrarEntrada;
  /** Para exibir na tela sem depender do cardápio. */
  resumo: {
    itens: { produtoId: string; nome: string; quantidade: number; precoUnitCentavos: number }[];
    totalCentavos: number;
    formaPagamento: FormaPagamento;
  };
  /** PENDENTE: aguardando envio. RECUSADA: o servidor recusou e precisa de ação. */
  estado: "PENDENTE" | "RECUSADA";
  /** 0 = ainda na primeira tentativa (online); >0 = já ficou na fila. */
  tentativas: number;
  criadaLocalEm: string;
  erro?: ErroDaVenda;
};

interface Esquema extends DBSchema {
  fila: { key: string; value: VendaNaFila; indexes: { porUsuario: string } };
}

let conexao: Promise<IDBPDatabase<Esquema>> | undefined;
function banco() {
  conexao ??= openDB<Esquema>("caixaflow", 1, {
    upgrade(db) {
      db.createObjectStore("fila", { keyPath: "id" }).createIndex("porUsuario", "usuarioId");
    },
  });
  return conexao;
}

const ouvintes = new Set<() => void>();
const canal = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("caixaflow-fila") : null;
canal?.addEventListener("message", () => ouvintes.forEach((f) => f()));

function avisar() {
  ouvintes.forEach((f) => f());
  canal?.postMessage("mudou");
}

/** Chama `fn` sempre que a fila mudar (nesta aba ou em outra). */
export function aoMudarFila(fn: () => void): () => void {
  ouvintes.add(fn);
  return () => ouvintes.delete(fn);
}

export async function listarFila(usuarioId: string): Promise<VendaNaFila[]> {
  const itens = await (await banco()).getAllFromIndex("fila", "porUsuario", usuarioId);
  return itens.sort((a, b) => a.criadaLocalEm.localeCompare(b.criadaLocalEm));
}

export async function adicionarNaFila(venda: VendaNaFila): Promise<void> {
  await (await banco()).put("fila", venda);
  avisar();
}

export async function removerDaFila(id: string): Promise<void> {
  await (await banco()).delete("fila", id);
  avisar();
}

/** Atualiza só se ainda existir — evita recriar uma venda que outro envio já removeu. */
export async function atualizarNaFila(id: string, mudar: (v: VendaNaFila) => VendaNaFila): Promise<void> {
  const tx = (await banco()).transaction("fila", "readwrite");
  const atual = await tx.store.get(id);
  if (atual) await tx.store.put(mudar(atual));
  await tx.done;
  if (atual) avisar();
}
